/**
 * BRXWrapperEntry
 *
 * Entry controller for the embeddables wrapper section.
 * Inspects BRXSession, decides which surface to render, and orchestrates
 * the fresh-intake boot sequence (overlay → flow detection → provider load).
 *
 * Entry modes:
 *   returning_checkout_ready  — valid checkout URL exists; show BRXCheckoutReturn
 *   returning_checkout_stale  — checkout complete but URL unresolvable; show BRXCheckoutReturn
 *   welcome_screen            — first visit (welcome not yet answered); show welcome → overlay → app
 *   fresh_intake              — welcome answered, no checkout; skip welcome + overlay, load app directly
 *
 * Deps are injected so each module can be tested in isolation.
 *
 * Usage (from section inline script):
 *   window.BRXWrapperEntry.start(sectionRoot, {
 *     overlay:            window.BRXWrapperOverlay,
 *     loader:             window.BRXEmbeddablesLoader,
 *     returnUi:           window.BRXCheckoutReturn,
 *     flow:               window.BRXFlowRuntime,
 *     welcome:            window.BRXWelcomeScreen,
 *     checkoutSubmitting: window.BRXCheckoutSubmitting
 *   });
 */
(function () {
    var SOFT_WAIT_MS    = 10000;
    var MAX_WAIT_MS     = 20000;
    var WARMUP_MS       = 5000;
    var SUBMIT_TIMEOUT_MS = 20000;

    // Load-failure recovery: one silent retry for transient loader failures
    // before surfacing a "Try again" button; copy escalates past this many
    // total attempts (auto + manual).
    var AUTO_RETRY_LIMIT        = 1;
    var ESCALATE_AFTER_ATTEMPTS = 3;

    // Stages that are worth silently retrying once before bothering the user.
    // 'bad_payload' (malformed /init response) is deterministic, not transient.
    // 'flow_timeout' (script+fetch succeeded but the flow never mounted) has
    // no natural point to re-arm a second timeout after a silent retry, so it
    // goes straight to the manual "Try again" UI instead.
    var SILENT_RETRY_STAGES = { script_load: true, fetch_timeout: true, fetch_error: true };

    var STAGE_COPY = {
        script_load:   { heading: 'We’re having trouble loading', message: 'There was a problem loading the intake. Please try again.' },
        fetch_timeout: { heading: 'This is taking longer than expected', message: 'The connection timed out. Please try again.' },
        fetch_error:   { heading: 'We’re having trouble loading', message: 'There was a problem loading the intake. Please try again.' },
        bad_payload:   { heading: 'We’re having trouble loading', message: 'Something went wrong loading the intake. Please try again.' },
        flow_timeout:  { heading: 'This is taking longer than expected', message: 'The intake didn’t finish loading. Please try again.' }
    };
    var ESCALATED_MESSAGE = 'Still having trouble — try refreshing the page, or check back in a few minutes.';

    // --------------------------------------------------
    // Editor detection (kept here — affects entry decision)
    // --------------------------------------------------

    function isEditorMode() {
        try { if (window.Shopify && window.Shopify.designMode) return true; } catch (e) {}
        try {
            var sp = new URLSearchParams(window.location.search);
            if (
                sp.has('preview_theme_id') ||
                sp.has('design_theme_id') ||
                sp.has('pb') ||
                sp.get('_fd') === '0'
            ) return true;
        } catch (e) {}
        try { if (/^admin\.shopify\.com$/i.test(window.location.hostname)) return true; } catch (e) {}
        return false;
    }

    // --------------------------------------------------
    // Entry-mode decision
    // --------------------------------------------------

    function getEntryMode() {
        var session = window.BRXSession;
        if (!session || !session.getCheckoutReturnState) return 'welcome_screen';
        var rs = session.getCheckoutReturnState();
        if (rs.mode === 'ready') return 'returning_checkout_ready';
        if (rs.mode === 'stale') return 'returning_checkout_stale';
        var state = session.getState ? session.getState() : {};
        if (state.brxFlowPage === 'savvy' || state.brxFlowPage === 'checkout_ready') return 'fresh_intake';
        return 'welcome_screen';
    }

    // --------------------------------------------------
    // Public: start
    // --------------------------------------------------

    function start(root, deps) {
        // --- Guard: Shopify editor (no session or deps needed) --------------
        if (isEditorMode()) {
            var welcomeEl = root.querySelector('[data-intake-welcome]');
            if (welcomeEl) welcomeEl.classList.remove('is-hidden');
            return;
        }

        if (!window.BRXSession) {
            window.addEventListener('brx:session-ready', function () {
                start(root, deps);
            }, { once: true });
            return;
        }

        deps = deps || {};
        var BRXWrapperOverlay     = deps.overlay            || window.BRXWrapperOverlay;
        var BRXEmbeddablesLoader  = deps.loader             || window.BRXEmbeddablesLoader;
        var BRXCheckoutReturn     = deps.returnUi           || window.BRXCheckoutReturn;
        var BRXFlowRuntime        = deps.flow               || window.BRXFlowRuntime;
        var BRXWelcomeScreen      = deps.welcome            || window.BRXWelcomeScreen;
        var BRXCheckoutSubmitting = deps.checkoutSubmitting || window.BRXCheckoutSubmitting;

        var savvyId     = root.getAttribute('data-savvy-id')    || '';
        var fallbackUrl = root.getAttribute('data-fallback-url') || '';

        // Create the overlay controller once; it may be reused after "Resume intake".
        var overlay = BRXWrapperOverlay ? BRXWrapperOverlay.create(root) : null;

        // Final-submit feedback (progress overlay + error banner over the host).
        var submittingUI    = BRXCheckoutSubmitting ? BRXCheckoutSubmitting.mount(root) : null;
        var submitTimeoutId = null;

        function clearSubmitTimeout() {
            if (submitTimeoutId) { clearTimeout(submitTimeoutId); submitTimeoutId = null; }
        }

        // --- Guard: missing savvy ID ----------------------------------------
        if (!savvyId) {
            root.classList.add('is-warmup');
            if (overlay) {
                overlay.setHeading('Missing flow configuration');
                overlay.setMessage('Add a Savvy flow ID in the section settings.');
                overlay.setProgress(0);
            }
            return;
        }

        // --- Entry-mode branch ---------------------------------------------
        var entryMode = getEntryMode();

        if (entryMode === 'returning_checkout_ready' || entryMode === 'returning_checkout_stale') {
            if (BRXCheckoutReturn) {
                BRXCheckoutReturn.mount(root, {
                    mode: entryMode === 'returning_checkout_ready' ? 'ready' : 'stale',
                    onResume: function () {
                        startFreshIntake(root, {
                            overlay:     overlay,
                            loader:      BRXEmbeddablesLoader,
                            flow:        BRXFlowRuntime,
                            savvyId:     savvyId,
                            fallbackUrl: fallbackUrl,
                            skipOverlay: true
                        });
                    }
                });
            }
        } else if (entryMode === 'welcome_screen') {
            if (BRXWelcomeScreen) {
                BRXWelcomeScreen.mount(root, {
                    onAnswered: function () {
                        if (window.BRXSession && window.BRXSession.setBrxFlowPage) {
                            window.BRXSession.setBrxFlowPage('savvy');
                        }
                        window.scrollTo(0, 0);
                        startFreshIntake(root, {
                            overlay:     overlay,
                            loader:      BRXEmbeddablesLoader,
                            flow:        BRXFlowRuntime,
                            savvyId:     savvyId,
                            fallbackUrl: fallbackUrl,
                            skipOverlay: false
                        });
                    }
                });
            }
        } else {
            // fresh_intake — welcome already answered; show overlay while flow loads
            startFreshIntake(root, {
                overlay:     overlay,
                loader:      BRXEmbeddablesLoader,
                flow:        BRXFlowRuntime,
                savvyId:     savvyId,
                fallbackUrl: fallbackUrl,
                skipOverlay: false
            });
        }

        // --- Final-submit feedback -------------------------------------------
        // Checkout.js (running inside the Embeddables host) dispatches these
        // around its lead-update + session-complete calls. The progress overlay
        // layers on top of the still-mounted host; the safety timer guards
        // against a submit that never resolves if a request hangs silently.
        window.addEventListener('brx:checkout-submitting', function () {
            if (submittingUI) submittingUI.showSubmitting('Submitting prescription...');
            clearSubmitTimeout();
            submitTimeoutId = setTimeout(function () {
                if (submittingUI) submittingUI.showError({ errorType: 'unknown', message: 'This is taking longer than expected. Please try again, or start over.' });
            }, SUBMIT_TIMEOUT_MS);
        });

        window.addEventListener('brx:checkout-submitting-error', function (event) {
            clearSubmitTimeout();
            var detail = event && event.detail;
            if (submittingUI) {
                if (detail && detail.message) submittingUI.showError(detail);
                else submittingUI.hideSubmitting();
            }
        });

        // --- In-session checkout completion ---------------------------------
        // brx-session.js dispatches this after POST /checkout/session resolves
        // (or immediately with the dosable URL as fallback). Mount the return
        // surface in place — no page navigation required.
        window.addEventListener('brx:checkout-ready', function () {
            clearSubmitTimeout();
            if (submittingUI) {
                submittingUI.hideSubmitting();
                submittingUI.hideError();
            }
            if (BRXCheckoutReturn) {
                BRXCheckoutReturn.mount(root, {
                    mode: 'ready',
                    onResume: function () {
                        startFreshIntake(root, {
                            overlay:     overlay,
                            loader:      BRXEmbeddablesLoader,
                            flow:        BRXFlowRuntime,
                            savvyId:     savvyId,
                            fallbackUrl: fallbackUrl,
                            skipOverlay: true
                        });
                    }
                });
            }
        }, { once: true });

        // --- bfcache restoration fix ----------------------------------------
        // When the user navigates to checkout and presses Back, the browser may
        // restore this page from bfcache in whatever visual state it was in at
        // departure. Re-evaluate session state on pageshow and snap the correct
        // surface into view.
        window.addEventListener('pageshow', function (event) {
            if (!event.persisted) return;
            var session = window.BRXSession;
            if (!session) return;

            var rs = session.getCheckoutReturnState();

            if (rs.mode === 'ready' || rs.mode === 'stale') {
                var overlayEl = root.querySelector('[data-intake-overlay]');
                var hostEl    = root.querySelector('[data-intake-host]');
                var welcomeEl = root.querySelector('[data-intake-welcome]');
                if (overlayEl) overlayEl.classList.add('is-hidden');
                if (hostEl)    hostEl.classList.add('is-hidden');
                if (welcomeEl) welcomeEl.classList.add('is-hidden');
                root.classList.remove('is-warmup');
                if (submittingUI) {
                    submittingUI.hideSubmitting();
                    submittingUI.hideError();
                }
                if (BRXCheckoutReturn) {
                    BRXCheckoutReturn.mount(root, {
                        mode: rs.mode === 'ready' ? 'ready' : 'stale',
                        onResume: function () {
                            startFreshIntake(root, {
                                overlay:     overlay,
                                loader:      BRXEmbeddablesLoader,
                                flow:        BRXFlowRuntime,
                                savvyId:     savvyId,
                                fallbackUrl: fallbackUrl,
                                skipOverlay: true
                            });
                        }
                    });
                }
            } else if (session.getState && session.getState().brxFlowPage !== 'welcome') {
                // Past welcome — ensure overlay and welcome are hidden.
                var overlayEl2 = root.querySelector('[data-intake-overlay]');
                var welcomeEl2 = root.querySelector('[data-intake-welcome]');
                if (overlayEl2) overlayEl2.classList.add('is-hidden');
                if (welcomeEl2) welcomeEl2.classList.add('is-hidden');
            }
        });
    }

    // --------------------------------------------------
    // Fresh-intake boot sequence
    // --------------------------------------------------

    function startFreshIntake(root, deps) {
        var overlay     = deps.overlay;
        var loader      = deps.loader;
        var flow        = deps.flow;
        var savvyId     = deps.savvyId;
        var fallbackUrl = deps.fallbackUrl;
        var skipOverlay = deps.skipOverlay || false;
        var hostEl      = root.querySelector('[data-intake-host]');

        var done          = false;
        var warmupDone    = skipOverlay;  // already "done" when skipping
        var intakeReady   = false;
        var revealed      = false;
        var revealedRef   = { value: false };
        var timeouts      = [];
        var loadAttempt      = 0;
        var manualRetryCount = 0;
        var errorShown       = false;  // suppresses the generic "Almost ready" narration once a real error is on screen

        root.classList.remove('is-return');
        if (!skipOverlay) root.classList.add('is-warmup');

        function clearTimers() {
            for (var i = 0; i < timeouts.length; i++) clearTimeout(timeouts[i]);
            timeouts = [];
        }

        function buildFallbackUrl(reason) {
            if (!fallbackUrl) return '';
            try {
                var fb = new URL(fallbackUrl, window.location.origin);
                fb.searchParams.set('from', 'embeddables_wrapper');
                fb.searchParams.set('reason', reason || 'timeout');
                fb.searchParams.set('path', window.location.pathname + window.location.search + window.location.hash);
                return fb.toString();
            } catch (e) { return fallbackUrl; }
        }

        // Kept as an escape hatch but intentionally not called anywhere below —
        // recovery from a load failure is in-place retry (see handleLoadError/
        // showLoadErrorUI), not a redirect. fallbackUrl also has no default and
        // isn't populated anywhere today, so this has been a no-op regardless.
        // If a real fallback_url is ever configured, decide deliberately whether
        // to wire this back in rather than restoring it silently.
        function goFallback(reason) {
            if (done || !fallbackUrl) return;
            done = true;
            clearTimers();
            window.removeEventListener('online',  onOnline);
            window.removeEventListener('offline', onOffline);
            window.location.href = buildFallbackUrl(reason);
        }

        function maybeReveal() {
            if (done || revealed || !warmupDone || !intakeReady) return;
            revealed        = true;
            revealedRef.value = true;
            done            = true;
            clearTimers();
            window.removeEventListener('online',  onOnline);
            window.removeEventListener('offline', onOffline);
            if (overlay) overlay.hide();
            root.classList.remove('is-warmup');
            root.classList.add('is-ready');
            if (hostEl) hostEl.classList.remove('is-hidden');
        }

        function onOnline() {
            if (done) return;
            if (warmupDone && !intakeReady && overlay) {
                overlay.setHeading('Almost ready');
                overlay.setMessage('Back online. Finalizing your intake\u2026');
            }
        }

        function onOffline() {
            if (done || !overlay) return;
            overlay.setHeading('Connection interrupted');
            overlay.setMessage('You appear to be offline. We\u2019ll keep trying\u2026');
        }

        window.addEventListener('online',  onOnline);
        window.addEventListener('offline', onOffline);

        // Build and start enhancements (click handler + session subscription)
        var enhCtx = window.BRXFlowEnhancements
            ? window.BRXFlowEnhancements.create({ savvyId: savvyId, sectionRoot: root })
            : null;
        if (enhCtx) enhCtx.start();

        // Warmup overlay animation (first-time visitors only)
        if (overlay && !skipOverlay) {
            overlay.showWarmup(function onWarmupComplete() {
                warmupDone = true;
                maybeReveal();
            });
        }

        // Flow root detection
        if (flow) {
            flow.start({
                enhancements:   enhCtx ? enhCtx.enhancements  : [],
                activeFlowRef:  enhCtx ? enhCtx.activeFlowRef : { current: null },
                revealedRef:    revealedRef,
                onFlowDetected: function () {
                    intakeReady = true;
                    if (loadAttempt > 1 && window.BRXSessionAnalytics && window.BRXSessionAnalytics.pushIntakeLoadRecovered) {
                        window.BRXSessionAnalytics.pushIntakeLoadRecovered(loadAttempt);
                    }
                    maybeReveal();
                }
            });
        }

        // Load and initialise the Embeddables provider script
        function attemptLoad() {
            if (!loader) return;
            loadAttempt++;
            loader.load(root, { onError: handleLoadError });
        }

        function showLoadErrorUI(stage) {
            if (done || !overlay) return;
            errorShown = true;
            var copy    = STAGE_COPY[stage] || STAGE_COPY.fetch_error;
            var message = loadAttempt >= ESCALATE_AFTER_ATTEMPTS ? ESCALATED_MESSAGE : copy.message;
            overlay.showError({
                heading: copy.heading,
                message: message,
                onRetry: onRetryClick
            });
        }

        function handleLoadError(detail) {
            if (done) return;
            var stage = (detail && detail.stage) || 'fetch_error';

            if (window.BRXSessionAnalytics && window.BRXSessionAnalytics.pushIntakeLoadError) {
                window.BRXSessionAnalytics.pushIntakeLoadError(stage, loadAttempt);
            }

            if (loadAttempt <= AUTO_RETRY_LIMIT && SILENT_RETRY_STAGES[stage]) {
                attemptLoad();
                return;
            }

            showLoadErrorUI(stage);
        }

        function onRetryClick() {
            if (done) return;
            manualRetryCount++;
            if (window.BRXSessionAnalytics && window.BRXSessionAnalytics.pushIntakeLoadRetry) {
                window.BRXSessionAnalytics.pushIntakeLoadRetry(manualRetryCount);
            }
            attemptLoad();
        }

        attemptLoad();

        // Timeout messaging + hard fallback (only meaningful when overlay is shown)
        if (!skipOverlay) {
            timeouts.push(setTimeout(function () {
                if (done || warmupDone || errorShown || !navigator.onLine) return;
                if (overlay) {
                    overlay.setHeading('Almost ready');
                    overlay.setMessage('We\u2019re preparing the next step.');
                }
            }, Math.max(0, WARMUP_MS - 1500)));
        }

        timeouts.push(setTimeout(function () {
            if (done || intakeReady || errorShown) return;
            if (!navigator.onLine) {
                if (overlay && !skipOverlay) {
                    overlay.setHeading('Connection interrupted');
                    overlay.setMessage('You appear to be offline. We\u2019ll keep trying\u2026');
                }
                return;
            }
            if (overlay && !skipOverlay) {
                overlay.setHeading('Almost ready');
                overlay.setMessage('Finalizing your intake\u2026');
            }
        }, SOFT_WAIT_MS));

        timeouts.push(setTimeout(function () {
            if (done) return;
            if (!navigator.onLine) {
                if (overlay && !skipOverlay) {
                    overlay.setHeading('Connection interrupted');
                    overlay.setMessage('Still offline. Please check your connection and try again.');
                }
                return;
            }
            handleLoadError({ stage: 'flow_timeout' });
        }, MAX_WAIT_MS));
    }

    window.BRXWrapperEntry = {
        getEntryMode: getEntryMode,
        start: start
    };
}());
